import React from 'react'

import ActionsBox from '@/app/module/campaigns/component/actions/actions-box'
import { useCampaignActionContext } from '@/app/module/campaigns/component/item-provider'
import { SMS_ACTION_DEFAULT_SENDERID } from '@/app/module/campaigns/definitions'
import {
  CampaignTemplateType,
  ChoiceValueMultiple,
  Language,
  LocalAction,
  LocalOnAnswerType,
  PartFamily,
  ReplyType,
  SenderIdOption,
} from '@/app/module/campaigns/types'
import { getActionOptionsForCampaign } from '@/app/module/campaigns/utils/actions/options'
import { ActionContext } from '@/app/module/campaigns/utils/actions/types'
import { CountryTimezone, FilesType, PersonalizationType, TimezoneWithCountryCodeType } from '@/app/types'

import ChoiceTagMultiple from './choice-tag-multiple'
import { GetImageProps, GetVoiceProps } from './part-config-props'

type Props = {
  actions: LocalAction[]
  activeSpeechLang: string
  countryCode: string
  countriesTimezones: CountryTimezone[]
  defaultLanguage: string
  files: FilesType
  hasTranscribe: boolean
  id: string
  index: number
  isCloseDisabled: boolean
  label: string
  languages: Language[]
  orgId: number
  personalizationList: PersonalizationType
  questionIndex: number
  replies: ReplyType[]
  senderIds: SenderIdOption[]
  token: string
  timezone: string
  timezones: TimezoneWithCountryCodeType
  transcribeEnabled: boolean
  type: CampaignTemplateType

  getImageProps: GetImageProps
  getVoiceProps: GetVoiceProps
  onChange: (value: Partial<LocalOnAnswerType>, speechLang: string, index: number) => void
  onClose: (index: number) => void
}

const ResponseVoiceSpeechItem: React.FC<Props> = ({
  actions,
  activeSpeechLang,
  countriesTimezones,
  countryCode,
  defaultLanguage,
  files,
  hasTranscribe,
  id,
  index,
  isCloseDisabled,
  label,
  languages,
  orgId,
  personalizationList,
  questionIndex,
  replies,
  senderIds,
  timezone,
  timezones,
  token,
  transcribeEnabled,
  type,
  getImageProps,
  getVoiceProps,
  onChange,
  onClose,
}) => {
  const { hasQuotas } = useCampaignActionContext()
  const handleActionsChange = React.useCallback(
    (actionsList: LocalAction[]) => {
      onChange({ actions: actionsList }, activeSpeechLang, index)
    },
    [activeSpeechLang, index, onChange],
  )
  const handleChoiceChange = React.useCallback(
    (newValue: ChoiceValueMultiple) => onChange(newValue, activeSpeechLang, index),
    [activeSpeechLang, index, onChange],
  )
  const handleLabelChange = React.useCallback(
    (newLabel: string) => onChange({ label: newLabel }, activeSpeechLang, index),
    [activeSpeechLang, index, onChange],
  )
  const handleRemove = React.useCallback(() => onClose(index), [index, onClose])

  const actionOptions = React.useMemo(
    () =>
      getActionOptionsForCampaign({
        campaignType: type,
        partType: PartFamily.Voice,
        hasQuotas,
        actionNamespace: ActionContext.OnAnswer,
      }),
    [hasQuotas, type],
  )

  const value = React.useMemo(() => ({ ranges: [], replies, speech: true }), [replies])

  return (
    <ActionsBox
      actionOptions={actionOptions}
      actions={actions}
      countriesTimezones={countriesTimezones}
      countryCode={countryCode}
      defaultLanguage={defaultLanguage}
      defaultSenderId={SMS_ACTION_DEFAULT_SENDERID.voiceQuestion}
      key={index}
      files={files}
      hasTranscribe={hasTranscribe}
      index={questionIndex}
      isCloseDisabled={isCloseDisabled}
      label={label}
      languages={languages}
      orgId={orgId}
      personalizationList={personalizationList}
      senderIds={senderIds}
      timezone={timezone}
      timezones={timezones}
      token={token}
      transcribeEnabled={transcribeEnabled}
      updateContactConfig={updateContactConfig}
      voiceId={`${id}-${index}`}
      whatsappSenderIds={[]}
      onChange={handleActionsChange}
      onLabelChange={handleLabelChange}
      getImageProps={getImageProps}
      getVoiceProps={getVoiceProps}
      onClose={handleRemove}
    >
      <ChoiceTagMultiple
        data-testid={`voice-speech-choice-input-${activeSpeechLang}-${index}`}
        value={value}
        onChange={handleChoiceChange}
      />
    </ActionsBox>
  )
}

const updateContactConfig = {
  invalid: false,
  openResponse: false,
  question: true,
}

export default React.memo(ResponseVoiceSpeechItem)
